import { SocketContext, useSocket } from '@/context/SocketContext';
import React, { ReactNode, useContext, useEffect, useRef } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { UserRole, Permission } from '@/types';

interface ProtectedRouteProps {
  children?: ReactNode;
  // Для обратной совместимости
  isAuthenticated?: boolean;
  // Новые props для ролей
  allowedRoles?: UserRole[];
  requiredPermissions?: Permission[];
  fallback?: ReactNode;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated: legacyIsAuthenticated,
  allowedRoles,
  requiredPermissions,
  fallback,
  redirectTo,
}) => {
  const { isAuthenticated, role, hasAnyPermission } = useAuth();
  const location = useLocation();
  const { socket } = useSocket();

  // Используем новую логику аутентификации или legacy prop
  const userIsAuthenticated =
    legacyIsAuthenticated !== undefined
      ? legacyIsAuthenticated
      : isAuthenticated;

  // Socket логика (оставляем как есть)
  // useEffect(() => {
  //   if (socket) {
  //     const token = localStorage.getItem('accessToken');
  //     if (token) {
  //       socket.auth = {
  //         ...socket.auth,
  //         token: `Bearer ${token}`,
  //       };
  //       socket.connect();
  //     } else {
  //       console.debug('No token found in localStorage');
  //     }
  //   }
  // }, [socket]);

  // Если не авторизован - на страницу логина
  if (!userIsAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Проверка ролей
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    // Редирект в зависимости от роли
    const defaultRedirect = getDefaultRedirectByRole(role);
    return <Navigate to={redirectTo || defaultRedirect} replace />;
  }

  // Проверка прав доступа
  if (requiredPermissions && !hasAnyPermission(requiredPermissions)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    const defaultRedirect = role ? getDefaultRedirectByRole(role) : '/login';
    return <Navigate to={redirectTo || defaultRedirect} replace />;
  }

  // Если есть children - рендерим их, иначе Outlet для nested routes
  return <>{children || <Outlet />}</>;
};

// Хелпер для получения дефолтного редиректа по роли
const getDefaultRedirectByRole = (role: UserRole): string => {
  switch (role) {
    case UserRole.OWNER:
    case UserRole.MANAGER:
      return '/dashboard';
    case UserRole.TENANT:
      return '/';
    default:
      return '/login';
  }
};

export default ProtectedRoute;
